/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import { useAuth } from "../context/AuthContext";

/**
 * Organisation selector, in the navbar the way WorkDrive places it.
 *
 * Renders nothing when the user has no organisation, and renders a plain label
 * (no chevron, not clickable) when they have exactly one — a control that only
 * ever has one option is a lie about what the product can do.
 */
const OrgSwitcher = () => {
  const theme = useTheme();
  const { organisations, activeOrgId, activeOrg, switchOrganisation } = useAuth();
  const [anchor, setAnchor] = useState(null);
  const [switching, setSwitching] = useState(null);

  if (organisations.length === 0) return null;

  const label = activeOrg?.name || activeOrgId || "Organisation";

  if (organisations.length === 1) {
    return (
      <Typography
        sx={{ fontSize: 13, fontWeight: 600, color: theme.palette.text.color, px: 1 }}
        title="You belong to one organisation"
      >
        {label}
      </Typography>
    );
  }

  const choose = async (organisationId) => {
    if (organisationId === activeOrgId) {
      setAnchor(null);
      return;
    }
    setSwitching(organisationId);
    try {
      await switchOrganisation(organisationId);
    } finally {
      setSwitching(null);
      setAnchor(null);
    }
  };

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-label="Switch organisation"
        onClick={(e) => setAnchor(e.currentTarget)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setAnchor(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          px: 1,
          py: 0.5,
          borderRadius: "8px",
          cursor: "pointer",
          color: theme.palette.text.color,
          "&:hover": { backgroundColor: theme.palette.tertiary.main },
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label}</Typography>
        <ArrowDropDownIcon
          sx={{ fontSize: 20, transform: anchor ? "rotate(180deg)" : "none", transition: "0.2s" }}
        />
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 260 } } }}
      >
        <Typography
          sx={{ px: 2, py: 1, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em",
                textTransform: "uppercase", color: theme.palette.secondaryText.color }}
        >
          Organisations
        </Typography>
        {organisations.map((org) => {
          const active = org.organisation_id === activeOrgId;
          return (
            <MenuItem
              key={org.organisation_id}
              selected={active}
              disabled={Boolean(switching)}
              onClick={() => choose(org.organisation_id)}
            >
              <ListItemText
                primary={org.name}
                secondary={org.role}
                primaryTypographyProps={{ fontSize: 13.5 }}
                secondaryTypographyProps={{ fontSize: 11, textTransform: "capitalize" }}
              />
              {switching === org.organisation_id ? (
                <CircularProgress size={16} sx={{ color: "accent.main" }} />
              ) : (
                active && <CheckIcon sx={{ fontSize: 18, color: "accent.main" }} />
              )}
            </MenuItem>
          );
        })}
        <Box sx={{ px: 2, py: 1 }}>
          <Chip
            size="small"
            label="Switching reloads Drive"
            sx={{ fontSize: 10.5, color: theme.palette.secondaryText.color }}
          />
        </Box>
      </Menu>
    </>
  );
};

export default OrgSwitcher;
